"use client";

import { useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import "../landing.css";
import { useLang } from "../i18n";
import SiteNav from "@/components/marketing/SiteNav";
import SiteFooter from "@/components/marketing/SiteFooter";
import ThemedCapture from "@/components/marketing/ThemedCapture";
import PreviewSteps, { type PreviewStep } from "./PreviewSteps";
import ScoreFilm, { type FilmChapter } from "./ScoreFilm";
import LayerTabs, { type LayerView } from "./LayerTabs";
import DossierFan from "./DossierFan";
import MeasuredTimeline from "./MeasuredTimeline";
import { CAPTURE, LAYER_KEYS } from "@/lib/previewCapture";
import { START_HREF } from "@/lib/routes";
import { trackCta } from "@/lib/analytics";

/* ────────────────────────────────────────────────────────────────────────────
 *  Preview — the one place a visitor sees the product before signing up.
 *
 *  The page SHOWS rather than tells (2026-09-08: the version before this one
 *  had grown two text grids — sixteen register cards and six "what it
 *  measures" cards — and Josh's verdict was "just more text"). In order:
 *
 *    1. The stage — the 3D map with the scored building, full width.
 *    2. How it works — a FILM of a score being made, recorded from the app:
 *       the address typed in, the map landing, the registers answering, the
 *       verdict, Location opened with the map answering it. The chapter strip
 *       under it is the whole "how"; the page says nothing else about it
 *       (2026-09-09: the caption line under it was clutter, Josh's word).
 *    3. Three steps on the rail — the score, the layers (switched the way the
 *       product switches them), the dossier (three real pages, fanned).
 *    4. Why it is more than a search — the measurement series behind this
 *       score drawn on a year axis: the point is that several of the numbers
 *       took years to accumulate and were never written down anywhere a
 *       chatbot could read them. Then the same point made concretely, in a
 *       four-row "by hand vs PlotScore" strip.
 *    5. The City Atlas poster, and the close.
 *
 *  EVERY number on the page is read from src/data/preview-capture.json, the
 *  sidecar the capture session writes beside the images (lib/previewCapture)
 *  — composite, layer dots, chapter times, the film's speed factor, every bar
 *  on the timeline. Nothing is typed into this file (§Marketing claims), so a
 *  re-capture that moves the score moves the page with it. All images and
 *  the film come from that ONE session — see scripts/capture-product-
 *  sidebar.ts on why one session is the whole point.
 * ──────────────────────────────────────────────────────────────────────────── */

export function PreviewContent() {
  const { lang } = useLang();
  const nl = lang === "nl";
  const c = CAPTURE;

  useEffect(() => {
    document.body.classList.add("landing-body");
    return () => {
      document.body.classList.remove("landing-body");
    };
  }, []);

  const layerLabel: Record<(typeof LAYER_KEYS)[number], string> = nl
    ? { valuation: "Waardering", building: "Gebouw", location: "Locatie", neighborhood: "Buurt", investment: "Investering" }
    : { valuation: "Valuation", building: "Building", location: "Location", neighborhood: "Neighborhood", investment: "Investment" };

  const layerViews: LayerView[] = LAYER_KEYS.map((key) => ({
    key,
    label: layerLabel[key],
    score: c.layers[key],
    src: `/product/sidebar-${key}.png`,
    alt: nl
      ? `De laag ${layerLabel[key]} geopend voor ${c.address}: elke factor met zijn meting, score en bron.`
      : `The ${layerLabel[key]} layer opened for ${c.address}: every factor with its measurement, score and register.`,
  }));

  const chapters: FilmChapter[] = nl
    ? [
        { key: "search", label: "Zoeken", detail: "Elk adres in Nederland." },
        { key: "scan", label: "De scan", detail: "Elke bron antwoordt. Versneld." },
        { key: "verdict", label: "Het oordeel", detail: "Eén getal, zijn oordeel, zijn betrouwbaarheid." },
        { key: "layer", label: "Locatie", detail: "Open een laag; de kaart onderbouwt hem." },
      ]
    : [
        { key: "search", label: "Search", detail: "Any address in the Netherlands." },
        { key: "scan", label: "The scan", detail: "Every source answers. Sped up." },
        { key: "verdict", label: "The verdict", detail: "One number, its tier, its confidence." },
        { key: "layer", label: "Location", detail: "Open a layer; the map argues it." },
      ];

  const steps: PreviewStep[] = [
    {
      n: "01",
      eyebrow: nl ? "De score" : "The score",
      title: nl ? "Eén getal, en de vijf erachter." : "One number, and the five behind it.",
      body: nl
        ? "Oordeel, percentiel en betrouwbaarheid naast het getal. Vijf lagen eronder."
        : "Tier, percentile and confidence beside the number. Five layers under it.",
      src: "/product/sidebar-overview.png",
      alt: nl
        ? `Het PlotScore-paneel: de samengestelde score ${c.composite} voor ${c.address} met oordeel, databetrouwbaarheid en de vijf laagrijen.`
        : `The PlotScore panel: the composite ${c.composite} for ${c.address} with its tier, data confidence and the five layer rows.`,
      width: 590,
      height: 1568,
      // The overview is short, so the panel's pinned footer leaves ~700 px
      // of empty middle in the capture. Show only the part that speaks.
      contentHeight: 840,
      shape: "tall",
    },
    {
      n: "02",
      eyebrow: nl ? "De lagen" : "The layers",
      title: nl ? "Open elke laag." : "Open any layer.",
      body: nl
        ? "Elke factor met zijn meting, zijn score en zijn bron."
        : "Every factor with its measurement, its score and its source.",
      src: "/product/sidebar-building.png",
      alt: "",
      width: 590,
      height: 1568,
      shape: "tall",
      figure: <LayerTabs layers={layerViews} width={590} height={1568} />,
    },
    {
      n: "03",
      eyebrow: nl ? "Het dossier" : "The dossier",
      title: nl ? "Neem het mee." : "Take it with you.",
      body: nl
        ? "Zes delen, elke bron benoemd. Voor een bank, een makelaar of een gemeente."
        : "Six parts, every source named. For a bank, a broker or a gemeente.",
      note: nl
        ? "Gratis exports dragen een preview-watermerk; Buyer's Pass haalt het weg."
        : "Free exports carry a preview watermark; Buyer's Pass removes it.",
      src: "/product/step-report.png",
      alt: "",
      width: 1240,
      height: 1754,
      shape: "tall",
      figure: (
        <DossierFan
          pages={[
            { src: "/product/step-report.png", alt: nl ? `De omslag van het dossier voor ${c.address}.` : `The cover of the dossier for ${c.address}.`, width: 1240, height: 1754 },
            { src: "/product/dossier-p2.png", alt: nl ? "Deel II van het dossier: inzichten en aandachtspunten." : "Part II of the dossier: insights and watchouts.", width: 992, height: 1403 },
            { src: "/product/dossier-p3.png", alt: nl ? "Deel III van het dossier: het profiel met plattegrond en WOZ-verloop." : "Part III of the dossier: the profile with the site plan and the WOZ trajectory.", width: 992, height: 1403 },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="font-brand antialiased">
      <SiteNav locale={lang} active="preview" altHref={nl ? "/preview" : "/nl/preview"} />

      <main>
        {/* ── Header: one line, then the product ── */}
        <section className="pt-28 sm:pt-36 pb-10 sm:pb-14 text-center">
          <div className="max-w-[820px] mx-auto px-5 sm:px-8">
            <p className="text-[0.75rem] text-accent-ink uppercase tracking-[0.18em] mb-6">
              {nl ? "Productpreview" : "Product preview"}
            </p>
            <h1 className="font-display text-[2.6rem] sm:text-[3.6rem] md:text-[4.25rem] font-semibold text-ink leading-[1.02] tracking-[-0.01em] mb-6">
              {nl ? "Dit is PlotScore." : "This is PlotScore."}
            </h1>
            <p className="text-[1.05rem] sm:text-[1.2rem] text-ink-2 leading-[1.6] max-w-[600px] mx-auto">
              {nl
                ? "Eén adres, live gescoord uit 25+ databronnen. Opgenomen uit de app — niets is een mockup."
                : "One address, scored live from 25+ data sources. Recorded from the app — nothing here is a mockup."}
            </p>
            <p className="mt-7 inline-flex items-center gap-2 font-data text-[0.65rem] text-ink-muted uppercase tracking-[0.14em]">
              <span className="w-1.5 h-1.5 rounded-full bg-accent motion-safe:animate-pulse" aria-hidden="true" />
              {c.address}, {c.city}
            </p>
          </div>
        </section>

        {/* ── The stage: the map, the building, the score on it ── */}
        <section className="pb-20 sm:pb-28">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
            <FadeIn>
              <figure className="rounded-2xl border border-edge bg-surface p-1.5 sm:p-2 shadow-[0_50px_140px_-50px_rgb(0_0_0/0.65)]">
                <ThemedCapture
                  src="/product/step-map.jpg"
                  alt={nl
                    ? `Het PlotScore-werkblad: ${c.address} geselecteerd op de fotorealistische 3D-kaart van Amsterdam, met de score ${c.composite} op het pand zelf.`
                    : `The PlotScore workspace: ${c.address} selected on the photoreal 3D map of Amsterdam, its score of ${c.composite} carried on the building itself.`}
                  width={2880}
                  height={1800}
                  sizes="(min-width: 1200px) 1136px, 100vw"
                  loading="eager"
                  className="w-full h-auto rounded-xl"
                />
              </figure>
            </FadeIn>
          </div>
        </section>

        {/* ── How it works: the film ── */}
        {c.film && (
          <section className="py-20 sm:py-28 border-t border-edge">
            <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
              <div className="max-w-[640px] mb-10 sm:mb-12">
                <FadeIn>
                  <p className="text-[0.75rem] text-accent-ink uppercase tracking-[0.18em] mb-5">
                    {nl ? "Hoe het werkt" : "How it works"}
                  </p>
                </FadeIn>
                <FadeIn delay={0.05}>
                  <h2 className="font-display text-[1.9rem] sm:text-[2.5rem] font-semibold text-ink leading-[1.15] tracking-[0.005em]">
                    {nl ? "Kijk hoe een score ontstaat." : "Watch a score being made."}
                  </h2>
                </FadeIn>
              </div>
              <FadeIn delay={0.12}>
                <ScoreFilm
                  src="/video/preview/score.mp4"
                  poster="/video/preview/score-poster.jpg"
                  film={c.film}
                  chapters={chapters}
                  playLabel={nl ? "Speel af" : "Play"}
                />
              </FadeIn>
            </div>
          </section>
        )}

        {/* ── The score, the layers, the dossier — on one rail ── */}
        <PreviewSteps steps={steps} />

        {/* ── Why it is more than a search ── */}
        <section className="py-20 sm:py-28 border-t border-edge">
          <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
            <div className="max-w-[680px] mb-10 sm:mb-14">
              <FadeIn>
                <p className="text-[0.75rem] text-accent-ink uppercase tracking-[0.18em] mb-5">
                  {nl ? "Meer dan zoeken" : "More than a search"}
                </p>
              </FadeIn>
              <FadeIn delay={0.05}>
                <h2 className="font-display text-[1.9rem] sm:text-[2.5rem] font-semibold text-ink leading-[1.15] tracking-[0.005em]">
                  {nl ? "Sommige van deze getallen kostten tien jaar." : "Some of these numbers took a decade to make."}
                </h2>
              </FadeIn>
              <FadeIn delay={0.1}>
                <p className="mt-5 text-[1rem] text-ink-2 leading-[1.65]">
                  {nl
                    ? "Een chatbot antwoordt uit wat er over een straat geschreven is. PlotScore leest wat er op het adres gemeten is."
                    : "A chatbot answers from what was written about a street. PlotScore reads what was measured at the address."}
                </p>
              </FadeIn>
            </div>
            <FadeIn delay={0.12}>
              <MeasuredTimeline capture={c} nl={nl} />
            </FadeIn>

            {/* The same point, concretely: what four of those answers cost
                to get any other way. Kept to four rows — this is the one
                text block on the page, and it earns its place by being
                checkable (the EGMS download really is ~1.2 GB, the
                WOZ-waardeloket really does answer one address at a time). */}
            <div className="mt-16 sm:mt-20">
              <FadeIn>
                <p className="font-data text-[0.62rem] uppercase tracking-[0.18em] text-ink-muted mb-4">
                  {nl ? "Dezelfde vier antwoorden, op een andere manier" : "The same four answers, any other way"}
                </p>
              </FadeIn>
              <ByHand nl={nl} />
            </div>
          </div>
        </section>

        {/* ── City Atlas: the whole town, a real capture ── */}
        <section className="py-20 sm:py-28 border-t border-edge">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
            <div className="text-center max-w-[720px] mx-auto mb-10 sm:mb-14">
              <FadeIn>
                <p className="text-[0.75rem] text-accent-ink uppercase tracking-[0.18em] mb-5">Studio</p>
              </FadeIn>
              <FadeIn delay={0.05}>
                <h2 className="font-display text-[1.9rem] sm:text-[2.5rem] font-semibold text-ink leading-[1.15] tracking-[0.005em]">
                  {nl ? "De hele stad, gescoord." : "The whole city, scored."}
                </h2>
              </FadeIn>
            </div>
            <FadeIn delay={0.1}>
              <div className="rounded-2xl border border-edge bg-surface p-2 sm:p-3 shadow-[0_40px_120px_-40px_rgb(0_0_0/0.6)]">
                <Image
                  src="/product/atlas-amsterdam-poster.jpg"
                  alt={nl ? "City Atlas: elk pand in Amsterdam, gekleurd op score" : "City Atlas: every building in Amsterdam, coloured by score"}
                  width={1440}
                  height={900}
                  sizes="(min-width: 1200px) 1136px, 100vw"
                  className="w-full h-auto rounded-xl"
                />
              </div>
              <p className="mt-4 text-center font-data text-[0.65rem] text-ink-muted uppercase tracking-[0.14em]">
                {nl ? "City Atlas · elk pand in Amsterdam, gescoord · onderdeel van Studio" : "City Atlas · every building in Amsterdam, scored · part of Studio"}
              </p>
            </FadeIn>
          </div>
        </section>

        {/* ── Close ── */}
        <section className="py-24 sm:py-32 border-t border-edge text-center">
          <div className="max-w-[720px] mx-auto px-5 sm:px-8">
            <FadeIn>
              <h2 className="font-display text-[2.2rem] sm:text-[3rem] font-semibold text-ink leading-[1.1] tracking-[0.005em] mb-8">
                {nl ? (
                  <>Begin met <em className="italic font-normal text-accent-ink">drie gratis scores</em>.</>
                ) : (
                  <>Start with <em className="italic font-normal text-accent-ink">three free scores</em>.</>
                )}
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  href={START_HREF}
                  onClick={() => trackCta("preview-cta")}
                  className="inline-flex items-center px-7 py-3.5 bg-accent text-on-accent text-[0.95rem] font-semibold rounded-lg hover:bg-accent-hover active:scale-[0.98] transition-all"
                >
                  {nl ? "Maak gratis account" : "Create free account"}
                </Link>
                <Link
                  href={nl ? "/nl/pricing" : "/pricing"}
                  className="inline-flex items-center gap-1.5 text-[0.9rem] text-ink-2 hover:text-ink transition-colors"
                >
                  {nl ? "Bekijk de prijzen" : "See pricing"}
                  <svg viewBox="0 0 16 16" className="w-3 h-3 stroke-current fill-none" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 8h10M9 4l4 4-4 4" />
                  </svg>
                </Link>
              </div>
            </FadeIn>
          </div>
        </section>
      </main>

      <SiteFooter locale={lang} />
    </div>
  );
}

/**
 * Four measurements, and what each costs any other way. The middle column
 * is deliberately factual about a chatbot: it is not that it lies, it is
 * that nothing here was ever written down for it to read.
 */
function ByHand({ nl }: { nl: boolean }) {
  const e = CAPTURE.evidence;
  const buildings = e.viewshed?.buildings?.toLocaleString(nl ? "nl-NL" : "en-GB") ?? null;
  const rows: { q: string; ai: string; hand: string; ps: string }[] = nl
    ? [
        { q: "Beweegt de bodem hier?", ai: "Staat nergens geschreven", hand: "1,2 GB radartegels downloaden en herprojecteren", ps: e.groundMotion ? `${e.groundMotion.period}, gemeten, in de score` : "Gemeten, in de score" },
        { q: "Wat deed de WOZ van élke woning in dit pand?", ai: "Citeert de straat", hand: "Eén adres per opzoeking bij het WOZ-waardeloket", ps: e.woz ? `${e.woz.points} jaar per woning, als grafiek` : "Per woning, per jaar, als grafiek" },
        { q: "Hoeveel lucht, en waar staat de muur?", ai: "Gokt uit foto's", hand: "Niet te doen zonder het 3D-stadsmodel", ps: buildings ? `Een zichtveld door ${buildings} gebouwen` : "Een zichtveld door de 3D-stad" },
        { q: "Welke beperkingen rusten op het object?", ai: "Weet het niet", hand: "Een betaald Kadaster-uittreksel", ps: e.wkpb != null ? `${e.wkpb} geregistreerd, met naam` : "Geregistreerd, met naam" },
      ]
    : [
        { q: "Is the ground moving here?", ai: "Not written anywhere", hand: "Download 1.2 GB of radar tiles and reproject them", ps: e.groundMotion ? `${e.groundMotion.period}, measured, in the score` : "Measured, in the score" },
        { q: "What did the WOZ of every unit in this building do?", ai: "Quotes the street", hand: "One address per lookup at the WOZ-waardeloket", ps: e.woz ? `${e.woz.points} years per unit, charted` : "Per unit, per year, charted" },
        { q: "How much sky, and which way is wall?", ai: "Guesses from photos", hand: "Not possible without the 3D city model", ps: buildings ? `A viewshed through ${buildings} buildings` : "A viewshed through the 3D city" },
        { q: "What restrictions are registered on the title?", ai: "Does not know", hand: "A paid Kadaster extract", ps: e.wkpb != null ? `${e.wkpb} registered, named` : "Registered, named" },
      ];
  const head = nl ? ["De vraag", "Een chatbot", "Zelf", "PlotScore"] : ["The question", "A chatbot", "By hand", "PlotScore"];
  return (
    <div className="overflow-x-auto -mx-5 sm:mx-0 px-5 sm:px-0">
      <table className="w-full min-w-[640px] text-left border-collapse">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={h} className={`py-2.5 pr-4 font-data text-[0.6rem] uppercase tracking-[0.14em] font-normal border-b border-edge ${i === 3 ? "text-accent-ink" : "text-ink-muted"}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.q} className="border-b border-edge align-top">
              <td className="py-3.5 pr-4 text-[0.9rem] text-ink leading-snug w-[30%]">{r.q}</td>
              <td className="py-3.5 pr-4 text-[0.85rem] text-ink-muted leading-snug">{r.ai}</td>
              <td className="py-3.5 pr-4 text-[0.85rem] text-ink-2 leading-snug">{r.hand}</td>
              <td className="py-3.5 text-[0.85rem] text-ink leading-snug">{r.ps}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FadeIn({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1], delay }}
    >
      {children}
    </motion.div>
  );
}
